import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Stack,
  useTheme,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { UserManagementTab } from './settings/UserManagementTab';
import { EntitySettingsTab } from './settings/EntitySettingsTab';
import { getAuthUser } from '../../services/authService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const [currentTab, setCurrentTab] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      setLoading(true);
      const user = await getAuthUser();

      if (!user) {
        setError('You must be logged in to access settings');
        return;
      }

      if (user.role !== 'admin' && user.role !== 'hr') {
        setError('Access denied. Admin or HR role required to access settings.');
        return;
      }

      setCurrentUser(user);
      setError('');
    } catch (err: any) {
      console.error('Error loading user:', err);
      setError(err.message || 'Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, px: 2 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }

  // HR can only access User Management tab
  const canAccessEntitySettings = currentUser?.role === 'admin';

  return (
    <Box sx={{ py: 4, backgroundColor: theme.palette.background.default, minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 4
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <SettingsIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight={700}>
                Settings
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 1 }}>
                Manage your organization and users
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Tabs */}
        <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              backgroundColor: theme.palette.grey[100],
              '& .MuiTab-root': {
                fontWeight: 600,
                minHeight: 64
              }
            }}
          >
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="User Management"
              id="settings-tab-0"
              aria-controls="settings-tabpanel-0"
            />
            {canAccessEntitySettings && (
              <Tab
                icon={<BusinessIcon />}
                iconPosition="start"
                label="Entity Settings"
                id="settings-tab-1"
                aria-controls="settings-tabpanel-1"
              />
            )}
          </Tabs>

          {/* Tab Panels */}
          <Box sx={{ p: 4 }}>
            <TabPanel value={currentTab} index={0}>
              <UserManagementTab />
            </TabPanel>

            {canAccessEntitySettings && (
              <TabPanel value={currentTab} index={1}>
                <EntitySettingsTab />
              </TabPanel>
            )}
          </Box>
        </Paper>

        {/* Info Box */}
        {!canAccessEntitySettings && (
          <Alert severity="info" sx={{ mt: 3 }}>
            You have access to User Management only. Contact an admin to modify entity settings.
          </Alert>
        )}
      </Container>
    </Box>
  );
};
